import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2 } from 'lucide-react';

const StepSizes = ({ formData, updateFormData, addWindow, removeWindow, updateWindow }) => (
  <div className="space-y-6">
    <div className="text-center mb-6">
      <h2 className="text-xl font-semibold text-gray-900">Window Measurements</h2>
      <p className="text-sm text-gray-500 mt-1">Tell us the size of your windows</p>
    </div>
    
    {/* Unit Toggle */}
    <div className="flex items-center justify-center gap-2 mb-4">
      <Button
        variant={formData.unit === 'mm' ? 'default' : 'outline'}
        size="sm"
        onClick={() => updateFormData('unit', 'mm')}
        className={formData.unit === 'mm' ? 'bg-[#8CB89F] hover:bg-[#7aa88f]' : ''}
      >
        mm
      </Button>
      <Button
        variant={formData.unit === 'cm' ? 'default' : 'outline'}
        size="sm"
        onClick={() => updateFormData('unit', 'cm')}
        className={formData.unit === 'cm' ? 'bg-[#8CB89F] hover:bg-[#7aa88f]' : ''}
      >
        cm
      </Button>
    </div>
    
    <p className="text-xs text-center text-amber-600 bg-amber-50 p-2 rounded-lg">
      <strong>Bay windows:</strong> Please measure each section of the bay individually and add the measurements together to get your total width
    </p>
    
    {/* Windows List */}
    <div className="space-y-4">
      {formData.windows.map((window, index) => (
        <div key={window.id} className="p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <div className="flex-1 mr-2">
              <Input
                type="text"
                value={window.label}
                onChange={(e) => updateWindow(window.id, 'label', e.target.value)}
                placeholder="Enter window name (e.g. Lounge, Bedroom 1)"
                className="font-medium"
                data-testid={`window-${index}-label`}
              />
            </div>
            {formData.windows.length > 1 && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => removeWindow(window.id)}
                className="text-red-500 hover:text-red-600"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-3">
            <div className="space-y-1">
              <Label className="text-xs">Width ({formData.unit.toUpperCase()})</Label>
              <Input
                type="number"
                value={window.width}
                onChange={(e) => updateWindow(window.id, 'width', e.target.value)}
                placeholder={formData.unit === 'mm' ? 'e.g. 1200' : 'e.g. 120'}
                data-testid={`window-${index}-width`}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Height ({formData.unit.toUpperCase()})</Label>
              <Input
                type="number"
                value={window.height}
                onChange={(e) => updateWindow(window.id, 'height', e.target.value)}
                placeholder={formData.unit === 'mm' ? 'e.g. 1500' : 'e.g. 150'}
                data-testid={`window-${index}-height`}
              />
            </div>
          </div>
          
          {/* Window Type Selector */}
          <div className="space-y-1">
            <Label className="text-xs">Window Type</Label>
            <Select
              value={window.windowType || 'flat'}
              onValueChange={(value) => updateWindow(window.id, 'windowType', value)}
            >
              <SelectTrigger className="w-full" data-testid={`window-${index}-type`}>
                <SelectValue placeholder="Select window type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="flat">Flat Window</SelectItem>
                <SelectItem value="bay">Bay Window</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      ))}
    </div>
    
    <Button 
      variant="outline" 
      onClick={addWindow}
      className="w-full"
      data-testid="add-window-btn"
    >
      <Plus className="w-4 h-4 mr-2" />
      Add Another Window
    </Button>
  </div>
);

export default StepSizes;
